/**
 * variant-media-gallery.js
 * Handles interactions within the product media gallery using variant-specific images.
 * Listens for 'variant-change' event to update visibility.
 * Handles thumbnail clicks to change the active media.
 * Assumes the presence of a global `subscribe` function and `variant-change` event name.
 * Assumes associated `slider-component` elements for viewer and thumbnails.
 */

// Define the event name directly using the known string value
const VARIANT_CHANGE_EVENT_NAME = 'variant-change';

if (!customElements.get('variant-media-gallery')) {
  customElements.define(
    'variant-media-gallery',
    class VariantMediaGallery extends HTMLElement {
      constructor() {
        super();
        // Cache DOM elements for performance
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewerComponent: this.querySelector('slider-component[id^="GalleryViewer-' + this.getSectionId() + '"]'),
          thumbnailsComponent: this.querySelector('slider-component[id^="GalleryThumbnails-' + this.getSectionId() + '"]'),
          viewerList: this.querySelector('[id^="Slider-Gallery"]'),
          thumbnailList: this.querySelector('[id^="Slider-Thumbnails"]'),
          counterCurrent: this.querySelector('.slider-counter--current'),
          counterTotal: this.querySelector('.slider-counter--total'),
          prevButton: this.querySelector('#GalleryViewer-' + this.getSectionId() + ' > .slider-buttons button[name="previous"]'),
          nextButton: this.querySelector('#GalleryViewer-' + this.getSectionId() + ' > .slider-buttons button[name="next"]'),
          thumbPrevButton: this.querySelector('#GalleryThumbnails-' + this.getSectionId() + ' > button[name="previous"]'),
          thumbNextButton: this.querySelector('#GalleryThumbnails-' + this.getSectionId() + ' > button[name="next"]'),
          viewerItems: [], // Populated in initMediaItems
          thumbnailItems: [], // Populated in initMediaItems
        };
        this.mql = window.matchMedia('(min-width: 750px)'); // Media query for layout changes
        this.variantMediaCounts = {}; // Stores media count per variant ID
        this.currentVariantId = null; // ID of the variant currently displayed
        this.visibleViewerItems = []; // Cache of currently visible <li> elements in the main viewer

        // Bind 'this' context for event handlers and methods ONCE
        this.onVariantDetailsChange = this.onVariantDetailsChange.bind(this);
        this.onSlideChanged = this.onSlideChanged.bind(this);
        this.onThumbnailClick = this.onThumbnailClick.bind(this);
        this.setActiveThumbnail = this.setActiveThumbnail.bind(this); // Bind setActiveThumbnail
        this.scrollThumbnailIntoView = this.scrollThumbnailIntoView.bind(this); // Bind the new method

        // Create the debounced scroll function
        this.debouncedScrollThumbnailIntoView = debounce(this.scrollThumbnailIntoView, 300); // 300ms delay, adjust as needed

        this.isManuallyScrolling = false; // Flag to track click-initiated scrolls
      }

      connectedCallback() {
        // Runs when the element is added to the DOM
        this.initVariantCounts(); // Load counts from embedded script
        this.initMediaItems(); // Cache references to media items

        // --- Subscribe to Variant Change Event ---
        if (typeof subscribe === 'function' && VARIANT_CHANGE_EVENT_NAME) {
             try {
                // Assumes 'subscribe' returns an unsubscribe function
                this.variantUnsubscriber = subscribe(VARIANT_CHANGE_EVENT_NAME, this.onVariantDetailsChange);
             } catch (e) {
                console.error(`VariantMediaGallery: Error subscribing to ${VARIANT_CHANGE_EVENT_NAME}:`, e);
             }
        } else {
            // Log errors if subscribe mechanism isn't available
            if (typeof subscribe !== 'function') console.error("VariantMediaGallery: Global 'subscribe' function not found.");
            if (!VARIANT_CHANGE_EVENT_NAME) console.error("VariantMediaGallery: Event name 'variant-change' is missing or undefined.");
        }

        // Listener for internal slider changes (may not fire if no overflow)
        this.elements.viewerComponent?.addEventListener('slideChanged', this.onSlideChanged);

        // Add click listeners to all thumbnail buttons
        this.elements.thumbnailList?.querySelectorAll('button.thumbnail').forEach(button => {
            button.addEventListener('click', this.onThumbnailClick);
        });

        // --- Set Initial State ---
        // Determine the initially selected variant based on server-rendered classes
        const initialActiveItem = this.elements.viewerList?.querySelector('.is-active[data-variant-id]');
        this.currentVariantId = initialActiveItem?.dataset.variantId || null;

        if (this.currentVariantId) {
            // Check if the initially selected variant actually has images defined
            const initialImageCount = this.variantMediaCounts[this.currentVariantId] || 0;
            if (initialImageCount > 0) {
               // If it has images, update visibility and state
               this.updateMediaVisibility(this.currentVariantId, true); // Pass true for initial load setup
            } else {
                // Handle case where the server rendered an active variant that has no images in our map
                console.warn(`VariantMediaGallery: Initially selected variant ${this.currentVariantId} has no custom images per JSON data. Gallery might appear empty or fallback needed.`);
                this.updateCountersAndButtons(); // Ensure counters show 0 / 0
            }
        } else {
            // Fallback if no '.is-active' class was rendered (e.g., first variant has no images)
            const firstItem = this.elements.viewerItems?.[0]; // Check the first item in the DOM
            if (firstItem?.dataset.variantId) {
                const firstVariantId = firstItem.dataset.variantId;
                const firstImageCount = this.variantMediaCounts[firstVariantId] || 0;
                if (firstImageCount > 0) {
                   // If the *first* variant in the DOM has images, show those
                   this.updateMediaVisibility(firstVariantId, true);
                } else {
                   // If even the first variant has no images
                   console.warn(`VariantMediaGallery: First available variant ${firstVariantId} also has no custom images.`);
                   this.updateCountersAndButtons(); // Ensure counters show 0 / 0
                }
            } else {
                // No media items found at all
                this.updateCountersAndButtons(); // Ensure counters show 0 / 0
            }
        }

        // Apply specific desktop layout adjustments if needed (e.g., removing list semantics for grid layouts)
        if (this.elements.thumbnailsComponent && this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
             this.removeListSemantic();
        }
      }

      disconnectedCallback() {
        // Runs when the element is removed from the DOM
        // Clean up event listeners and subscriptions
        if (this.variantUnsubscriber) {
           try { this.variantUnsubscriber(); } catch (e) { console.error("VariantMediaGallery: Error during unsubscribe:", e); }
        }
        this.elements.viewerComponent?.removeEventListener('slideChanged', this.onSlideChanged);
        // No need to explicitly remove thumbnail listeners if component is removed
      }

      getSectionId() {
        // Helper to consistently get the section ID
        return this.dataset.sectionId || this.id.replace('VariantMediaGallery-', ''); // Adapt if ID format changes
      }

      initVariantCounts() {
        // Parses the embedded JSON script tag containing variantId: mediaCount pairs
        const countScript = this.querySelector('script[data-variant-media-counts]');
        if (countScript) {
          try {
            this.variantMediaCounts = JSON.parse(countScript.textContent);
          } catch (e) {
            console.error('VariantMediaGallery: Failed to parse variant media counts.', e);
            this.variantMediaCounts = {}; // Default to empty object on error
          }
        } else {
            // Log warning if the script tag is missing
            console.warn('VariantMediaGallery: Variant media count data script not found.');
        }
      }

      initMediaItems() {
        // Caches NodeLists of viewer and thumbnail <li> elements for quicker access
         this.elements.viewerItems = Array.from(this.elements.viewerList?.querySelectorAll('li[data-variant-id]') || []);
         this.elements.thumbnailItems = Array.from(this.elements.thumbnailList?.querySelectorAll('li[data-variant-id]') || []);
         // Note: visibleViewerItems cache is updated within updateMediaVisibility
      }

      // --- Event Handlers ---

      onVariantDetailsChange(eventOrData) {
          // Handles the 'variant-change' event published by product-info.js
          // Extracts variant data - adapt based on your pub/sub library's data structure
          const eventData = eventOrData?.data || eventOrData?.detail?.data || eventOrData;
          const variant = eventData?.variant;

          if (!variant?.id) {
              console.warn("VariantMediaGallery: Received variant change event without valid variant ID from PubSub.");
              return; // Exit if no valid variant ID received
          };

          const newVariantId = variant.id.toString();
          // Look up the image count for the newly selected variant
          const newVariantImageCount = this.variantMediaCounts[newVariantId] || 0;

          // *** Only update the gallery if the new variant has associated images ***
          if (newVariantImageCount > 0) {
            this.updateMediaVisibility(newVariantId);
          } else {
            // If the newly selected variant has no images, do nothing - keep showing the previous one
            console.log(`VariantMediaGallery: New variant ${newVariantId} has no custom images. Gallery not updated.`);

          }
      }

      // Inside VariantMediaGallery class
      onSlideChanged(event) {
          // <<<--- CHECK FLAG FIRST ---<<<
          if (this.isManuallyScrolling) {
              return; // Ignore slide changes during the click-initiated scroll animation
          }
          // --- Check done, original logic continues below ---
      
          if (!event.detail.currentElement) return;
      
          const currentElement = event.detail.currentElement;
          const currentVariantId = currentElement.dataset.variantId;
      
          // Ignore if the event is for a slide not belonging to the currently active variant
          if (currentVariantId !== this.currentVariantId) return;
      
          // Update the counter display and sync the active thumbnail state
          this.updateCurrentCounter(currentElement);
          const currentMediaId = currentElement.dataset.mediaId;
          if (this.elements.thumbnailList && currentMediaId) {
              const targetThumbnail = this.elements.thumbnailList.querySelector(`li[data-target="${currentMediaId}"]`);
              // Ensure thumbnail exists and is visible before marking it active
              if (targetThumbnail && !targetThumbnail.classList.contains('small-hide')) {
                  this.setActiveThumbnail(targetThumbnail); // Update highlight
                   // Debounced scroll for thumbnail (handles user swipe/arrow on main slider)
                  this.debouncedScrollThumbnailIntoView(targetThumbnail);
              }
          }
          // Play media (e.g., video) in the newly active slide
          this.playActiveMedia(currentElement);
      }

      // Inside VariantMediaGallery class
      onThumbnailClick(event) {
          event.preventDefault();
          const thumbnailButton = event.currentTarget;
          const targetLi = thumbnailButton.closest('li');
      
          if (!targetLi || targetLi.classList.contains('small-hide')) return;
      
          const targetMediaId = targetLi.dataset.target;
          if (targetMediaId) {
               const targetSlide = this.elements.viewerList?.querySelector(`li[data-media-id="${targetMediaId}"]`);
      
               if (targetSlide && !targetSlide.classList.contains('small-hide')) {
                  const sliderElement = this.elements.viewerComponent?.slider; // The main gallery <ul>
      
                  // Update main slide active class
                  this.elements.viewerItems.forEach(item => item.classList.remove('is-active'));
                  targetSlide.classList.add('is-active');
      
                  // Update counter and highlight clicked thumbnail immediately
                  this.updateCurrentCounter(targetSlide);
                  this.setActiveThumbnail(targetLi); // Highlight clicked thumb NOW
      
                  // --- Scroll Main Slider and Manage Flag ---
                  if (sliderElement) {
                      // SET FLAG: Indicate we are starting a scroll initiated by click
                      this.isManuallyScrolling = true;
      
                      // Scroll the main slider (CSS likely makes this smooth)
                      sliderElement.scrollTo({
                          left: targetSlide.offsetLeft,
                          behavior: 'smooth' // Keep smooth, as 'auto' likely overridden by CSS
                      });
      
                      // RESET FLAG: After a delay longer than the expected scroll animation
                      // Increase timeout to be safe (e.g., 800ms or even 1000ms if animation is long)
                      clearTimeout(this.manualScrollTimeout); // Clear any previous timeout
                      this.manualScrollTimeout = setTimeout(() => {
                          this.isManuallyScrolling = false;
                      }, 800); // <-- Adjust duration if needed (try 800ms)
                  }
                  // --- End Scroll Main Slider ---
      
                  // Play media and handle header
                  this.playActiveMedia(targetSlide);
                  this.preventStickyHeader();
               }
          }
      }

      // --- Core Logic Methods ---

      updateMediaVisibility(selectedVariantId, isInitialLoad = false) {
        // Shows/hides media based on selected variant, updates caches, resets sliders.
        if (!isInitialLoad && this.currentVariantId === selectedVariantId) {
            return; // Avoid redundant processing
        }

        const HIDE_CLASSES = ['small-hide', 'medium-hide', 'large-up-hide']; // CSS classes used for hiding
        this.currentVariantId = selectedVariantId; // Set the new active variant ID
        let firstVisibleMediaItem = null; // Reference to the first <li> to become visible
        let firstVisibleThumbnailItem = null; // Reference to the first thumbnail <li> to become visible

        // Iterate through all viewer <li> items to toggle visibility
        this.elements.viewerItems.forEach(item => {
          const isMatch = item.dataset.variantId === selectedVariantId;
          // Toggle all hide classes based on whether the variant matches
          item.classList.toggle(HIDE_CLASSES[0], !isMatch);
          item.classList.toggle(HIDE_CLASSES[1], !isMatch);
          item.classList.toggle(HIDE_CLASSES[2], !isMatch);
          // Set 'is-active' only on the *first* item that matches the selected variant
          item.classList.toggle('is-active', isMatch && !firstVisibleMediaItem);

          if (isMatch && !firstVisibleMediaItem) {
              firstVisibleMediaItem = item; // Store the first visible item
          } else if (!isMatch) {
              item.classList.remove('is-active'); // Ensure non-matching items are not active
          }
        });

        // Iterate through all thumbnail <li> items to toggle visibility and aria-current
        this.elements.thumbnailItems.forEach(item => {
          const isMatch = item.dataset.variantId === selectedVariantId;
          item.classList.toggle(HIDE_CLASSES[0], !isMatch);
          item.classList.toggle(HIDE_CLASSES[1], !isMatch);
          item.classList.toggle(HIDE_CLASSES[2], !isMatch);
          const button = item.querySelector('button');
          if(button) {
              // Set 'aria-current' only on the *first* thumbnail that matches
              button.toggleAttribute('aria-current', isMatch && !firstVisibleThumbnailItem);
          }
          if(isMatch && !firstVisibleThumbnailItem) {
              firstVisibleThumbnailItem = item; // Store the first visible thumbnail
          } else if (!isMatch && button) {
              button.removeAttribute('aria-current'); // Ensure non-matching thumbs don't have aria-current
          }
        });

        // Update the cache of visible viewer items *after* toggling classes
        this.updateVisibleItemsCache();

        // Update counter display (total) and button enabled/disabled states immediately
        this.updateCountersAndButtons();

        // Use a minimal timeout to allow the DOM to update (classes applied)
        // before resetting the slider components, which rely on element dimensions.
        setTimeout(() => {
            // Tell slider components to recalculate pages based on visible items
            this.elements.viewerComponent?.resetPages();
            this.elements.thumbnailsComponent?.resetPages();

            // Scroll sliders back to the beginning instantly
            if (this.elements.viewerComponent?.slider) {
                this.elements.viewerComponent.slider.scrollTo({ left: 0, behavior: 'instant' });
            }
            if (this.elements.thumbnailsComponent?.slider) {
                this.elements.thumbnailsComponent.slider.scrollTo({ left: 0, behavior: 'instant' });
            }

             // --- Final State Check After Reset/Scroll ---
             if (firstVisibleMediaItem) {
                 // Re-assert active class on the first item, as resetPages might clear it
                 this.elements.viewerItems.forEach(item => item.classList.remove('is-active'));
                 firstVisibleMediaItem.classList.add('is-active');
                 // Ensure counter reads "1 / total"
                 this.updateCurrentCounter(firstVisibleMediaItem);
                 // Announce the first image for accessibility
                 this.announceLiveRegion(firstVisibleMediaItem, 1);
             } else {
                  // If no items became visible (should only happen if count was 0)
                  if(this.elements.counterCurrent) this.elements.counterCurrent.textContent = '0';
             }
             // Ensure the first thumbnail is correctly marked and potentially scrolled into view
             if (firstVisibleThumbnailItem) {
               this.setActiveThumbnail(firstVisibleThumbnailItem);
               // Scroll directly on initial load/variant change, no debounce
               this.scrollThumbnailIntoView(firstVisibleThumbnailItem);
                 
             }
        }, 0); // Timeout 0 typically sufficient for repaint
      }

      updateCountersAndButtons() {
          // Updates the total count display and enables/disables slider buttons
          const count = this.variantMediaCounts[this.currentVariantId] || 0;
          // Get slidesPerPage from the component if available, default to 1
          const viewerSlidesPerPage = this.elements.viewerComponent?.slidesPerPage || 1;
          // Determine if scrolling is possible in the main viewer
          const canScrollViewer = count > viewerSlidesPerPage;

          // Update total count display
          if (this.elements.counterTotal) {
              this.elements.counterTotal.textContent = count;
          }
          // Note: Current counter ("1" of "5") is updated elsewhere (on click or after visibility change)

          // Update main viewer button states
          this.elements.prevButton?.toggleAttribute('disabled', true); // Always disable 'prev' after variant change (we're at start)
          this.elements.nextButton?.toggleAttribute('disabled', !canScrollViewer); // Disable 'next' if not enough items to scroll

          // Update thumbnail slider button states
          const thumbThreshold = 4; // Number of thumbs visible before arrows needed (adjust based on layout)
          // Or dynamically: const thumbSlidesPerPage = this.elements.thumbnailsComponent?.slidesPerPage || thumbThreshold;
          const canScrollThumbs = count > thumbThreshold; // Or: count > thumbSlidesPerPage;
           if(this.elements.thumbPrevButton){
                this.elements.thumbPrevButton.toggleAttribute('disabled', true); // Always disable 'prev' initially
                // Also ensure visibility classes (set in Liquid based on TOTAL count) are respected
                // These lines might be redundant if Liquid handles initial state correctly, but safe to include
                this.elements.thumbPrevButton.classList.toggle('small-hide', count <= 3);
                this.elements.thumbPrevButton.classList.toggle('medium-hide', count <= 4);
                this.elements.thumbPrevButton.classList.toggle('large-up-hide', count <= 4);
           }
           if(this.elements.thumbNextButton){
                this.elements.thumbNextButton.toggleAttribute('disabled', !canScrollThumbs); // Disable 'next' if not enough thumbs
                // Ensure visibility classes are respected
                this.elements.thumbNextButton.classList.toggle('small-hide', count <= 3);
                this.elements.thumbNextButton.classList.toggle('medium-hide', count <= 4);
                this.elements.thumbNextButton.classList.toggle('large-up-hide', count <= 4);
           }
      }

      updateCurrentCounter(currentElement) {
          // Updates the 'current' part of the counter display (e.g., "2" in "2 / 5")
          if (!this.elements.counterCurrent || !currentElement) return;

          // Find the index of the currentElement within the VISIBLE items array
          const currentIndex = this.visibleViewerItems.findIndex(item => item === currentElement);

          if (currentIndex !== -1) {
              // Update text content (index is 0-based, display is 1-based)
              this.elements.counterCurrent.textContent = currentIndex + 1;
          } else {
              // Fallback if the element isn't found in the visible cache (should be rare)
              this.elements.counterCurrent.textContent = this.visibleViewerItems.length > 0 ? '1' : '0';
          }
      }

      updateVisibleItemsCache() {
          // Refreshes the `visibleViewerItems` array based on the `currentVariantId`
          if (!this.currentVariantId) {
              this.visibleViewerItems = []; // Clear cache if no variant selected
              return;
          }
          // Filter the full list of viewer items to include only those matching the current variant
          this.visibleViewerItems = this.elements.viewerItems.filter(item =>
               item.dataset.variantId === this.currentVariantId
          );
          // Note: We don't filter by CSS visibility (`.small-hide`) here,
          // because the cache needs to know all items *belonging* to the variant.
          // Visibility checks happen elsewhere when needed (e.g., finding the *first* visible item).
      }

      setActiveThumbnail(thumbnail) {
          // Sets the 'aria-current' attribute on the clicked thumbnail and scrolls the thumbnail slider if needed.
          if (!this.elements.thumbnailList || !thumbnail || thumbnail.classList.contains('small-hide')) return; // Ignore if hidden

          // Remove aria-current from all thumbnail buttons first
          this.elements.thumbnailItems.forEach(item => {
              item.querySelector('button')?.removeAttribute('aria-current');
          });
          // Add aria-current to the target thumbnail's button
          thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');

          // Scroll the thumbnail slider component if the active thumbnail is out of view
          const sliderComponent = this.elements.thumbnailsComponent;
          if (sliderComponent?.slider && thumbnail.offsetParent !== null) { // Check thumbnail is rendered and slider exists
              const sliderRect = sliderComponent.slider.getBoundingClientRect();
              const thumbnailRect = thumbnail.getBoundingClientRect();
              // If thumbnail is not fully visible within the slider's viewport
              if (thumbnailRect.left < sliderRect.left || thumbnailRect.right > sliderRect.right) {
                   // Use the slider component's own method to scroll if available
                   if (typeof sliderComponent.setSlidePosition === 'function') {
                        sliderComponent.setSlidePosition(thumbnail.offsetLeft);
                   } else {
                        // Fallback scroll method
                        sliderComponent.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
                   }
              }
          }
      }

      scrollThumbnailIntoView(thumbnail) {
        // Scrolls the thumbnail slider component if the active thumbnail is out of view.
        if (!this.elements.thumbnailsComponent?.slider || !thumbnail || thumbnail.offsetParent === null || thumbnail.classList.contains('small-hide')) return; // Check thumbnail is rendered/visible and slider exists

        const sliderComponent = this.elements.thumbnailsComponent;
        const sliderRect = sliderComponent.slider.getBoundingClientRect();
        const thumbnailRect = thumbnail.getBoundingClientRect();

        // If thumbnail is not fully visible within the slider's viewport
        if (thumbnailRect.left < sliderRect.left || thumbnailRect.right > sliderRect.right) {
           // Use the slider component's own method to scroll if available (prefer this if it works well)
           if (typeof sliderComponent.scrollToElement === 'function') {
                sliderComponent.scrollToElement(thumbnail, 'smooth'); // Assuming a method like this exists
           } else if (typeof sliderComponent.setSlidePosition === 'function') {
                sliderComponent.setSlidePosition(thumbnail.offsetLeft); // Original fallback
           } else {
                // Final fallback scroll method
                sliderComponent.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
           }
        }
      }

      announceLiveRegion(activeItem, position) {
         // Sends screen reader announcements when the active image changes.
         if (!this.elements.liveRegion || !activeItem) return; // Ensure elements exist
         const image = activeItem.querySelector('img'); // Find an image within the active item
         if (!image) return; // Exit if no image found

         // Function to perform the announcement
         const announce = () => {
             if (!this.elements.liveRegion) return; // Check again inside callback
             this.elements.liveRegion.setAttribute('aria-hidden', 'false'); // Make live region readable
             // Use translated string if available, otherwise provide a default
             const announcement = window.accessibilityStrings?.imageAvailable?.replace('[index]', position) || `Image ${position} available`;
             this.elements.liveRegion.innerHTML = announcement;
             // Hide the announcement after a short delay
             setTimeout(() => { if (this.elements.liveRegion) this.elements.liveRegion.setAttribute('aria-hidden', 'true'); }, 2000);
         };

         // Announce immediately if image is already loaded, otherwise wait for 'onload' event
         if (image.complete && image.naturalHeight !== 0) { // Check if loaded and has dimensions
             announce();
         } else {
             image.onload = announce;
             // Optional: Add error handling for failed image loads
             // image.onerror = () => console.error(`VariantMediaGallery: Image failed to load for announcement: ${image.src}`);
         }
       }

       playActiveMedia(activeItem) {
         // Initializes/plays deferred media (like videos or models) in the active slide.
         if (!activeItem) return;
         window.pauseAllMedia?.(); // Pause other media on the page if a global function exists
         // Find elements with the 'deferred-media' class within the active item
         const deferredMedia = activeItem.querySelector('.deferred-media');
         // If found and it has a 'loadContent' method (added by deferred-media.js)
         if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
             deferredMedia.loadContent(false); // Call method to load/play (parameter might control autoplay)
         }
       }

       preventStickyHeader() {
         // Dispatches an event to prevent sticky headers from overlapping the gallery during scroll adjustments.
         this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header'); // Cache sticky header element
         this.stickyHeader?.dispatchEvent(new Event('preventHeaderReveal')); // Dispatch event if element found
       }

       removeListSemantic() {
         // Removes list role/attributes if the gallery is displayed as a grid, for better accessibility.
         const sliderElement = this.elements.viewerComponent?.slider; // The main <ul>
         if (!sliderElement) return;
         sliderElement.setAttribute('role', 'presentation');
         // Treat each slide item as presentation only
         this.elements.viewerItems?.forEach((slide) => slide.setAttribute('role', 'presentation'));
       }
    }
  );
}

function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func.apply(this, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}